import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Shield,
  Lock,
  AlertTriangle,
  Menu,
  X,
  ChevronRight,
  Quote,
} from 'lucide-react';
import ContactModal from '../components/ContactModal';
import '../styles/Home.css';
import '../styles/TenRules.css';

const GUMROAD_URL = 'https://growyourbtc.gumroad.com/l/10rules';

const TenRules = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const openContactModal = () => {
    setMobileMenuOpen(false);
    setContactModalOpen(true);
  };

  return (
    <div className="gybtc-container gybtc-tr-page">
      {/* Header */}
      <header className={`gybtc-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="gybtc-header-content">
          <Link to="/" className="gybtc-logo-container">
            <img
              src="/gybtc-logo.png"
              alt="Grow Your BTC Logo"
              className="gybtc-logo"
            />
          </Link>

          <nav className={`gybtc-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <Link to="/" className="gybtc-nav-link" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/" className="gybtc-nav-link" onClick={() => setMobileMenuOpen(false)}>Free Guide</Link>
            <button onClick={() => scrollToSection('hero')} className="gybtc-nav-link">The 10 Rules</button>
            <button onClick={() => scrollToSection('book')} className="gybtc-nav-link">Book a Session</button>
            <button onClick={openContactModal} className="gybtc-nav-link">Contact</button>
          </nav>

          <button
            className="gybtc-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* HERO */}
      <section id="hero" className="gybtc-tr-hero">
        <div className="gybtc-hero-bg-watermark" aria-hidden="true">
          <span className="gybtc-hero-btc-mark">₿</span>
        </div>

        <div className="gybtc-tr-hero-content">
          <p className="gybtc-tr-eyebrow">The 10 Rules of Bitcoin · $9</p>

          <h1 className="gybtc-tr-hero-title">
  <span className="gybtc-tr-title-desktop">
    Protect What You Own
  </span>
  <span className="gybtc-tr-title-mobile">
    Protect What<br />You Own
  </span>
</h1>

<p className="gybtc-tr-hero-subtitle">
  Most beginners don't lose money from price.<br /><br />
  They lose it from mistakes — small ones that are easy to avoid,
  but costly if you don’t know what to look for.<br /><br />
  This guide shows you exactly what matters.
</p>

<p className="gybtc-tr-pre-cta">
  Built for beginners. No hype. No trading.
</p>

          <a
            href={GUMROAD_URL}
            className="gybtc-btn-primary gybtc-tr-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Instant Access – $9
            <ChevronRight size={20} />
          </a>

          <p className="gybtc-tr-hero-meta">
            One-time purchase · Instant access · Secure checkout
          </p>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="gybtc-tr-section gybtc-tr-problem">
        <div className="gybtc-tr-narrow">
          <div className="gybtc-tr-section-icon gybtc-tr-section-icon--warn">
            <AlertTriangle size={26} />
          </div>
          <p className="gybtc-tr-kicker">The Problem</p>
          <h2 className="gybtc-tr-section-title">
            Mistakes in Bitcoin are often <span className="gybtc-tr-accent">irreversible</span>
          </h2>

          <ul className="gybtc-tr-bullets">
            <li className="gybtc-tr-bullet">People leave Bitcoin on exchanges.</li>
            <li className="gybtc-tr-bullet">People lose access to their wallets.</li>
            <li className="gybtc-tr-bullet">People overcomplicate everything.</li>
            <li className="gybtc-tr-bullet">A small slip can cost everything.</li>
          </ul>

          <p className="gybtc-tr-closing">This guide exists to help you avoid that.</p>
        </div>
      </section>

      {/* WHAT THIS IS */}
      <section className="gybtc-tr-section gybtc-tr-what">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">What this is</p>
          <h2 className="gybtc-tr-section-title">
            A simple, practical guide based on <span className="gybtc-tr-accent">real experience</span>
          </h2>

          <p className="gybtc-tr-paragraph">
            No hype. No trading strategies. No noise.
          </p>
          <p className="gybtc-tr-paragraph">
            Just the rules that actually matter if you want to
            own and protect your Bitcoin long term.
          </p>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="gybtc-tr-section gybtc-tr-inside">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">What's inside</p>
          <h2 className="gybtc-tr-section-title">
            Everything you need, <span className="gybtc-tr-accent">nothing you don't</span>
          </h2>

          <ul className="gybtc-tr-checklist">
            {[
              'The 10 Rules of Bitcoin',
              'The Bitcoin Safety Checklist',
              'The Beginner Bitcoin Roadmap',
              'Common mistakes to avoid',
              'A simple 24-hour quick start plan',
            ].map((item) => (
              <li key={item} className="gybtc-tr-check-item">
                <span className="gybtc-tr-check-icon">
                  <Check size={16} strokeWidth={3} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="gybtc-tr-section gybtc-tr-who">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">Who this is for</p>
          <h2 className="gybtc-tr-section-title">
            Built for clarity, <span className="gybtc-tr-accent">not complexity</span>
          </h2>

          <ul className="gybtc-tr-bullets">
            <li className="gybtc-tr-bullet">Beginners who want clarity.</li>
            <li className="gybtc-tr-bullet">People who feel overwhelmed.</li>
            <li className="gybtc-tr-bullet">Anyone serious about long-term Bitcoin ownership.</li>
          </ul>
        </div>
      </section>

      {/* PRICE */}
      <section className="gybtc-tr-section gybtc-tr-price">
        <div className="gybtc-tr-narrow gybtc-tr-price-card">
          <p className="gybtc-tr-kicker">Price</p>
          <div className="gybtc-tr-price-amount">
            <span className="gybtc-tr-price-currency">$</span>
            <span className="gybtc-tr-price-num">9</span>
          </div>
          <p className="gybtc-tr-price-label">One-time purchase</p>

          <ul className="gybtc-tr-price-meta">
            <li>No subscriptions.</li>
            <li>No upsells.</li>
            <li>No nonsense.</li>
          </ul>

          <a
            href={GUMROAD_URL}
            className="gybtc-btn-primary gybtc-tr-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get The 10 Rules – $9
            <ChevronRight size={20} />
          </a>
        </div>
      </section>

      {/* MINI FAQ */}
      <section className="gybtc-tr-section gybtc-tr-faq">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">FAQ</p>
          <h2 className="gybtc-tr-section-title">A few quick questions</h2>

          <div className="gybtc-tr-faq-list">
            <details className="gybtc-tr-faq-item">
              <summary className="gybtc-tr-faq-q">
                Do I need technical knowledge?
                <span className="gybtc-tr-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-tr-faq-a">
                No. This is designed for beginners.
              </p>
            </details>

            <details className="gybtc-tr-faq-item">
              <summary className="gybtc-tr-faq-q">
                How do I receive the guide?
                <span className="gybtc-tr-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-tr-faq-a">
                Instant download after purchase via Gumroad.
              </p>
            </details>

            <details className="gybtc-tr-faq-item">
              <summary className="gybtc-tr-faq-q">
                Is this about trading?
                <span className="gybtc-tr-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-tr-faq-a">
                No. This focuses on protecting and owning your Bitcoin long term.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="gybtc-tr-section gybtc-tr-testimonial">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">What readers say</p>
          <div className="gybtc-tr-quote-icon">
            <Quote size={28} />
          </div>
          <blockquote className="gybtc-tr-quote">
            "It's MINE and in my OWN hardwallet. Thanks Jared…"
          </blockquote>
          <p className="gybtc-tr-quote-attr">— Early reader</p>
        </div>
      </section>

      {/* TRUST */}
      <section className="gybtc-tr-section gybtc-tr-trust">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">Why trust this</p>
          <h2 className="gybtc-tr-section-title">Simple, secure, and instant</h2>
        </div>

        <div className="gybtc-tr-narrow gybtc-tr-trust-grid">
          <div className="gybtc-tr-trust-card">
            <div className="gybtc-tr-trust-icon"><Lock size={22} /></div>
            <p className="gybtc-tr-trust-title">Secure checkout</p>
            <p className="gybtc-tr-trust-text">
              Powered by Gumroad — a trusted global platform.
            </p>
          </div>
          <div className="gybtc-tr-trust-card">
            <div className="gybtc-tr-trust-icon"><Shield size={22} /></div>
            <p className="gybtc-tr-trust-title">Instant access</p>
            <p className="gybtc-tr-trust-text">
              Delivered immediately after purchase.
            </p>
          </div>
        </div>

        <div className="gybtc-tr-cta-wrap">
          <a
            href={GUMROAD_URL}
            className="gybtc-btn-primary gybtc-tr-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Start Here – $9
            <ChevronRight size={20} />
          </a>
        </div>
      </section>

      {/* TRANSITION — 1:1 Guidance */}
      <section id="book" className="gybtc-tr-section gybtc-tr-book">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-kicker">1:1 Guidance</p>
          <h2 className="gybtc-tr-section-title">Prefer personal help?</h2>
          <p className="gybtc-tr-paragraph">
            Book a private session to get clarity on buying,
            securing, or planning your Bitcoin.
          </p>
          <a
            href="https://www.cal.eu/jaredtalbot/30min?user=jaredtalbot&overlayCalendar=true"
            className="gybtc-btn-outline gybtc-tr-outline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a Session
            <ChevronRight size={18} />
          </a>
        </div>
      </section>

      {/* FINAL LINE */}
      <section className="gybtc-tr-final">
        <div className="gybtc-tr-narrow">
          <p className="gybtc-tr-final-line">Build slowly.</p>
          <p className="gybtc-tr-final-line">Protect carefully.</p>
          <p className="gybtc-tr-final-line">Think long term.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="gybtc-footer">
        <div className="gybtc-footer-content">
          <p className="gybtc-footer-text">
            © 2025 Grow Your BTC{' • '}
            <Link to="/privacy">Privacy Policy</Link>{' • '}
            <Link to="/terms">Terms</Link>{' • '}
            <Link to="/disclaimer">Disclaimer</Link>{' • '}
            <Link to="/faq">FAQ</Link>
          </p>
        </div>
      </footer>

      <ContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </div>
  );
};

export default TenRules;
